import { c as _c } from "react/compiler-runtime";
// AutoLogout.js
import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import AlertDialog from '@/components/ui/AlertDialog';
import { forceLogout, LOGIN_TIMESTAMP_KEY } from '@/lib/auth';

// Constants
const MINUTE = 1000 * 60;
const CHECK_INTERVAL = MINUTE * 60; // check every hour
const WARNING_TIME = 5 * MINUTE; // Show warning 5 minutes before logout

const ForceLogout = () => {
  const $ = _c(17);
  const {
    isAuthenticated,
    logout,
    user,
    isLoading
  } = useAuth0();
  const [showWarning, setShowWarning] = useState(false);
  const loggedOut = !isLoading && !isAuthenticated;
  const [wasLoggedOut, setWasLoggedOut] = useState(loggedOut);
  if (wasLoggedOut !== loggedOut) {
    setWasLoggedOut(loggedOut);
    if (loggedOut) {
      setShowWarning(false);
    }
  }
  const [timeLeft, setTimeLeft] = useState(null);
  const warningShownRef = useRef(false);
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] !== logout) {
    t0 = () => {
      forceLogout(logout);
    };
    $[0] = logout;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleLogout = t0;
  let t1;
  let t2;
  if ($[2] !== handleLogout || $[3] !== isAuthenticated || $[4] !== isLoading || $[5] !== user) {
    t1 = () => {
      if (isLoading) {
        return;
      }
      if (!isAuthenticated) {
        localStorage.removeItem(LOGIN_TIMESTAMP_KEY);
        warningShownRef.current = false;
        return;
      }
      if (!user) {
        return;
      }
      const autoLogoutDuration = user?.["https://hasura.io/jwt/claims"]?.["x-hasura-session-timeout"] || null;
      if (!autoLogoutDuration || autoLogoutDuration === "0") {
        return;
      }
      const loginTimestamp = localStorage.getItem(LOGIN_TIMESTAMP_KEY);
      if (!loginTimestamp) {
        localStorage.setItem(LOGIN_TIMESTAMP_KEY, Date.now().toString());
        warningShownRef.current = false;
      }
      const checkSessionTime = () => {
        const storedTimestamp = localStorage.getItem(LOGIN_TIMESTAMP_KEY);
        if (!storedTimestamp) {
          return;
        }
        const elapsed = Date.now() - parseInt(storedTimestamp, 10);
        const totalDuration = autoLogoutDuration * MINUTE;
        const remainingTime = totalDuration - elapsed;
        setTimeLeft(remainingTime);
        if (remainingTime <= WARNING_TIME && remainingTime > 0 && !warningShownRef.current) {
          setShowWarning(true);
          warningShownRef.current = true;
        }
        if (remainingTime <= 0) {
          handleLogout();
        }
      };
      const firstCheckId = setTimeout(checkSessionTime, 0);
      const intervalId = setInterval(checkSessionTime, CHECK_INTERVAL);
      return () => {
        clearTimeout(firstCheckId);
        clearInterval(intervalId);
      };
    };
    t2 = [isAuthenticated, user, isLoading, handleLogout];
    $[2] = handleLogout;
    $[3] = isAuthenticated;
    $[4] = isLoading;
    $[5] = user;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
  let t3;
  bb0: {
    if (timeLeft === null) {
      t3 = undefined;
      break bb0;
    }
    const minutes = Math.max(1, Math.ceil(timeLeft / MINUTE));
    let t4;
    if ($[8] !== minutes) {
      t4 = moment.duration(minutes, "minutes").humanize();
      $[8] = minutes;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    t3 = t4;
  }
  const formattedTime = t3;
  if (!showWarning) {
    return null;
  }
  let t4;
  if ($[10] !== formattedTime || $[11] !== t) {
    t4 = t("general.you_will_soon_be_logged_out", {
      formattedTime
    });
    $[10] = formattedTime;
    $[11] = t;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setShowWarning(false);
    t6 = () => setShowWarning(false);
    $[13] = t5;
    $[14] = t6;
  } else {
    t5 = $[13];
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t4) {
    t7 = <AlertDialog open={true} title={t4} type="warning" onConfirm={t5} setOpen={t6} />;
    $[15] = t4;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
export default ForceLogout;